import styled from "@emotion/styled";

import { paper, fonts, easing, shade, inkA, redA, paperA } from "../theme";
import type { PromptMode } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsEyebrow, MsRuleRed,
    MsPaper, MsBtn, MsBackButton,
    MaskHeadline,
} from "../ms";

/* ──────────────────────────────────────────────────────────────────────
 * How the round's prompt gets chosen.
 *
 * Two ways to play, and they are not equal: the deck is the game, and digital
 * is what you reach for when someone forgot to bring it. The sheet says so —
 * physical is first, carries the recommendation, and is what a room starts on.
 *
 * Each option is drawn rather than described, because the difference is a
 * physical one: a card in your hand with a code on it, versus three cards on
 * the table to choose between. A paragraph explaining that reads much worse
 * than a picture of it.
 * ────────────────────────────────────────────────────────────────────── */

const ModeBody = styled(MsBody)`
    gap: 18px;
    padding-bottom: 24px;
`;

const Headline = styled(MsDisplay)`
    font-size: 38px;
    line-height: 0.98;
`;

const HeadlineRule = styled(MsRuleRed)`
    margin-top: 14px;
`;

const Options = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

/* The whole card is the control. `aria-pressed` rather than a radio group: two
 * mutually exclusive buttons read fine to a screen reader this way, and it keeps
 * the hit area the size of the card rather than the size of a dot. */
const Option = styled.button<{ selected: boolean }>`
    position: relative;
    display: block;
    width: 100%;
    text-align: left;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    transition: transform 0.18s ${easing.pop};

    &:hover:not(:disabled) { transform: translateY(-2px); }
    &:disabled { cursor: default; }
`;

const OptionPaper = styled(MsPaper)<{ selected: boolean }>`
    padding: 16px 18px 18px;
    border-radius: 6px;
    box-shadow:
        0 0 0 ${p => p.selected ? "2px" : "1px"} ${p => p.selected ? redA(0.75) : shade(0.12)},
        0 10px 22px -16px ${shade(0.45)};
    background: ${p => p.selected ? paper.paperBright : paper.paperCool};
    transition: box-shadow 0.18s ${easing.standard}, background 0.18s ${easing.standard};
`;

const OptionHead = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
`;

const OptionTitle = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 20px;
    line-height: 1.15;
    color: ${paper.ink};
`;

/* A stamp, not a badge — the house style for "this is the one". */
const Recommended = styled.div`
    font-family: ${fonts.sans};
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${paper.red};
    border: 1.5px solid ${redA(0.5)};
    border-radius: 3px;
    padding: 3px 6px;
    transform: rotate(-3deg);
    flex-shrink: 0;
`;

const OptionBlurb = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 14px;
    line-height: 1.4;
    color: ${paper.inkSoft};
    margin-top: 6px;
`;

const Figure = styled.div`
    margin: 14px 0 2px;
    display: flex;
    justify-content: center;
`;

const FigureSvg = styled.svg`
    width: 100%;
    max-width: 260px;
    height: auto;
    display: block;
`;

/* ── The infographics ──────────────────────────────────────────────────
 * Drawn from the same parts the app is: paper fills, a warm ink hairline, red
 * for the thing being pointed at. Flat, printed, no gradients — they should look
 * like a diagram on the back of the box, not an illustration. */

/** A card in your hand, its printed code, and the code typed into the phone. */
function PhysicalFigure() {
    return (
        <FigureSvg viewBox="0 0 260 92" role="img" aria-label="A card from the deck, its code typed into the phone">
            {/* the card */}
            <g transform="rotate(-6 56 46)">
                <rect x="10" y="18" width="92" height="60" rx="5"
                    fill={paper.paperBright} stroke={inkA(0.35)} strokeWidth="1.5" />
                <rect x="16" y="24" width="80" height="48" rx="3"
                    fill="none" stroke={inkA(0.15)} strokeWidth="1" />
                <text x="56" y="52" textAnchor="middle"
                    fontFamily={fonts.serif} fontWeight="800" fontSize="22" fill={paper.red}>NYR</text>
                <text x="56" y="65" textAnchor="middle"
                    fontFamily={fonts.sans} fontSize="7" letterSpacing="1.6" fill={inkA(0.45)}>PROMPT CARD</text>
            </g>

            {/* the hand-off */}
            <path d="M114 48 H136" stroke={inkA(0.4)} strokeWidth="1.5" strokeDasharray="3 3" />
            <path d="M133 44 L139 48 L133 52 Z" fill={inkA(0.5)} />

            {/* the phone, with the code going in */}
            <rect x="150" y="8" width="62" height="76" rx="8"
                fill={paper.paper} stroke={inkA(0.35)} strokeWidth="1.5" />
            <g>
                <rect x="160" y="34" width="13" height="18" rx="2" fill="none" stroke={paper.red} strokeWidth="1.5" />
                <rect x="175" y="34" width="13" height="18" rx="2" fill="none" stroke={paper.red} strokeWidth="1.5" />
                <rect x="190" y="34" width="13" height="18" rx="2" fill="none" stroke={inkA(0.25)} strokeWidth="1.5" />
                <text x="166" y="48" textAnchor="middle" fontFamily={fonts.serif} fontWeight="800" fontSize="13" fill={paper.ink}>N</text>
                <text x="181" y="48" textAnchor="middle" fontFamily={fonts.serif} fontWeight="800" fontSize="13" fill={paper.ink}>Y</text>
            </g>
            <rect x="164" y="62" width="34" height="4" rx="2" fill={inkA(0.12)} />
        </FigureSvg>
    );
}

/** Three cards face down, one turned, revealing the prompt. */
function DigitalFigure() {
    return (
        <FigureSvg viewBox="0 0 260 92" role="img" aria-label="Three face-down cards, one turned over to reveal a prompt">
            {/* the hand of three, fanned */}
            <g>
                <g transform="rotate(-9 34 52)">
                    <rect x="8" y="30" width="46" height="44" rx="4" fill={paper.red} stroke={shade(0.2)} strokeWidth="1" />
                    <rect x="12" y="34" width="38" height="36" rx="2" fill="none" stroke={paperA(0.45)} strokeWidth="1" />
                    <text x="31" y="58" textAnchor="middle" fontFamily={fonts.serif} fontStyle="italic"
                        fontWeight="800" fontSize="17" fill={paper.paper}>M</text>
                </g>
                <g>
                    <rect x="52" y="28" width="46" height="44" rx="4" fill={paper.red} stroke={shade(0.2)} strokeWidth="1" />
                    <rect x="56" y="32" width="38" height="36" rx="2" fill="none" stroke={paperA(0.45)} strokeWidth="1" />
                    <text x="75" y="56" textAnchor="middle" fontFamily={fonts.serif} fontStyle="italic"
                        fontWeight="800" fontSize="17" fill={paper.paper}>M</text>
                </g>
                {/* the one being turned — lifted, and the only one outlined in ink */}
                <g transform="rotate(9 118 48)">
                    <rect x="96" y="24" width="46" height="44" rx="4" fill={paper.red} stroke={paper.ink} strokeWidth="1.6" />
                    <rect x="100" y="28" width="38" height="36" rx="2" fill="none" stroke={paperA(0.45)} strokeWidth="1" />
                    <text x="119" y="52" textAnchor="middle" fontFamily={fonts.serif} fontStyle="italic"
                        fontWeight="800" fontSize="17" fill={paper.paper}>M</text>
                </g>
            </g>

            {/* turning over */}
            <path d="M150 46 H172" stroke={inkA(0.4)} strokeWidth="1.5" strokeDasharray="3 3" />
            <path d="M169 42 L175 46 L169 50 Z" fill={inkA(0.5)} />

            {/* the prompt, face up */}
            <rect x="184" y="22" width="68" height="48" rx="4"
                fill={paper.paperBright} stroke={inkA(0.35)} strokeWidth="1.5" />
            <rect x="192" y="34" width="30" height="3" rx="1.5" fill={inkA(0.55)} />
            <rect x="226" y="34" width="18" height="3" rx="1.5" fill={paper.red} />
            <rect x="192" y="44" width="24" height="3" rx="1.5" fill={inkA(0.55)} />
            <rect x="220" y="44" width="20" height="3" rx="1.5" fill={inkA(0.55)} />
            <rect x="192" y="54" width="34" height="3" rx="1.5" fill={inkA(0.3)} />
        </FigureSvg>
    );
}

const MODES: Array<{
    value: PromptMode;
    title: string;
    blurb: string;
    recommended?: boolean;
    Figure: () => React.ReactElement;
}> = [
    {
        value: "physical",
        title: "With the deck",
        blurb: "One player reads the code off a prompt card and types it in. The way the game is meant to be played.",
        recommended: true,
        Figure: PhysicalFigure,
    },
    {
        value: "digital",
        title: "No deck tonight",
        blurb: "Three cards face down on screen. Whoever's turn it is takes one, and that's the prompt.",
        Figure: DigitalFigure,
    },
];

interface Props {
    value: PromptMode;
    onChange: (mode: PromptMode) => void;
    onBack: () => void;
    ctaLabel?: string;
    onConfirm: () => void;
    /** In-game or non-host: show the choice without letting it change. */
    readOnly?: boolean;
}

export default function PaperGameMode({
    value, onChange, onBack, ctaLabel = "Done", onConfirm, readOnly,
}: Props) {
    return (
        <MsScreen>
            <ModeBody>
                <MsBackButton onClick={onBack} />

                <div>
                    <Headline>
                        <MaskHeadline>How you'll draw prompts.</MaskHeadline>
                    </Headline>
                    <HeadlineRule />
                </div>

                <MsEyebrow tone="mute">
                    {readOnly ? "Chosen for this game" : "Pick one"}
                </MsEyebrow>

                <Options>
                    {MODES.map(({ value: mode, title, blurb, recommended, Figure: Fig }) => (
                        <Option
                            key={mode}
                            type="button"
                            selected={value === mode}
                            aria-pressed={value === mode}
                            disabled={readOnly}
                            onClick={() => onChange(mode)}
                        >
                            <OptionPaper selected={value === mode}>
                                <OptionHead>
                                    <OptionTitle>{title}</OptionTitle>
                                    {recommended && <Recommended>How it's meant</Recommended>}
                                </OptionHead>
                                <OptionBlurb>{blurb}</OptionBlurb>
                                <Figure><Fig /></Figure>
                            </OptionPaper>
                        </Option>
                    ))}
                </Options>
            </ModeBody>

            <MsFooter>
                <MsBtn variant="red" onClick={onConfirm}>{ctaLabel}</MsBtn>
            </MsFooter>
        </MsScreen>
    );
}
